// REACT
import { SyntheticEvent, useEffect, useState } from 'react';

// YARN
import { Autocomplete, createFilterOptions, TextField } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// INTERFACE
import { IFormErrorObjects, IFormErrors } from 'components/generics/Interface/Commons/IErrorForm';

// THEMES
import { customAutocompleteTheme } from 'assets/styles/themes/Dialog/generic_autocomplete';

interface UseAutoCompleteParams<T> {
  data: T[];
  errorsFormDefault?: IFormErrors;
  field?: string;
  initialValue?: string | null;
  resetValue: () => void;
  selectedAssociateValue?: string | number | null;
  getOptionLabel: (option: T) => string;
  isMetaculture: boolean;
}

const useAutoComplete = <T,>({
  data,
  errorsFormDefault,
  field,
  initialValue,
  resetValue,
  selectedAssociateValue,
  getOptionLabel,
  isMetaculture,
}: UseAutoCompleteParams<T>) => {
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [options, setOptions] = useState<string[]>([]);
  const [metaCode, metaCultureName] = inputValue.split('-').map((part) => part.trim());
  const { t } = useTranslation();

  const filterOptions = createFilterOptions<T>({
    matchFrom: 'any',
    stringify: (option: T) => getOptionLabel(option),
  });

  const filterData = (value: string, data: T[]) => {
    const filtered =
      data &&
      filterOptions(data, {
        inputValue: value,
        getOptionLabel,
      });

    if (!value || filtered?.length === 0) {
      return data?.map((item) => getOptionLabel(item));
    }

    return filtered?.map((item) => getOptionLabel(item));
  };

  useEffect(() => {
    setOptions(filterData(inputValue, data));
  }, [inputValue, data]);

  const handleAutoCompleteChange = (e: SyntheticEvent, newValue: string | null) => {
    if (newValue) {
      setInputValue(newValue);
    } else {
      setInputValue('');
    }
  };

  const renderAutoComplete = () => {
    return (
      data &&
      data.length > 0 && (
        <ThemeProvider theme={customAutocompleteTheme}>
          <Autocomplete
            value={inputValue}
            onChange={handleAutoCompleteChange}
            options={options ?? []}
            onInputChange={(e, newInputValue) => {
              setInputValue(newInputValue);
            }}
            disabled={isMetaculture && selectedAssociateValue !== null}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('datagrid.search')}
                error={
                  isMetaculture &&
                  (errorsFormDefault?.[`${field}` as keyof IFormErrors] as IFormErrorObjects)
                    ?.message
                    ? true
                    : false
                }
                helperText={
                  isMetaculture &&
                  (errorsFormDefault?.[`${field}` as keyof IFormErrors] as IFormErrorObjects)
                    ?.message
                }
              />
            )}
            freeSolo
            slotProps={{
              clearIndicator: {
                onClick: () => {
                  resetValue();
                },
              },
            }}
            clearIcon={<ClearIcon fontSize="small" />}
          />
        </ThemeProvider>
      )
    );
  };

  return {
    renderAutoComplete,
    inputValue,
    metaCode,
    metaCultureName,
    setInputValue,
    filterData,
    setOptions,
  };
};

export default useAutoComplete;
